import React, { useState, useEffect } from 'react';
import { FaTruck, FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaPlus, FaSpinner, FaUser, FaCalendar, FaClock, FaPlay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { jobService } from '../../api/jobService';
import JobFormModal from '../../components/admin/JobFormModal';
import JobDetailsModal from '../../components/admin/JobDetailsModal';
import DeleteJobModal from '../../components/admin/DeleteJobModal';

const AdminScheduledJobs = () => {
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deletingJob, setDeletingJob] = useState(null);
  const [releasingJob, setReleasingJob] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);

  useEffect(() => {
    fetchScheduledJobs();
    fetchAvailableDrivers();
  }, [pagination.page, statusFilter, driverFilter, dateFilter]);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchScheduledJobs();
      } else if (searchTerm === '') {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchScheduledJobs();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchScheduledJobs = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(driverFilter !== 'all' && { driverId: driverFilter }),
        ...(dateFilter === 'today' && { 
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }),
        ...(dateFilter === 'week' && { 
          startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        }),
        ...(dateFilter === 'month' && { 
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        })
      };

      const response = await jobService.getAllScheduledJobs(params);
      
      if (response.success) {
        setScheduledJobs(response.data.scheduledJobs);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load scheduled jobs');
      }
    } catch (error) {
      console.error('Error fetching scheduled jobs:', error);
      toast.error('Failed to load scheduled jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const response = await jobService.getAvailableDrivers();
      if (response.success) {
        setAvailableDrivers(response.data.drivers || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDeleteJob = (job) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (jobId) => {
    try {
      setDeletingJob(jobId);
      const response = await jobService.deleteScheduledJob(jobId);
      
      if (response.success) {
        setScheduledJobs(prev => prev.filter(job => job.id !== jobId));
        toast.success('Scheduled job deleted successfully');
        setShowDeleteModal(false);
        setSelectedJob(null);
      } else {
        toast.error(response.message || 'Failed to delete scheduled job');
      }
    } catch (error) {
      console.error('Error deleting scheduled job:', error);
      toast.error('Failed to delete scheduled job. Please try again.');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleReleaseJob = async (jobId) => {
    try {
      setReleasingJob(jobId);
      const response = await jobService.releaseScheduledJob(jobId);
      
      if (response.success) {
        // Update the job status in the list
        setScheduledJobs(prev => prev.map(job => 
          job.id === jobId ? { ...job, status: 'RELEASED' } : job
        ));
        
        toast.success('Scheduled job released successfully! The driver has been notified.');
      } else {
        toast.error(response.message || 'Failed to release scheduled job');
      }
    } catch (error) {
      console.error('Error releasing scheduled job:', error);
      toast.error('Failed to release scheduled job. Please try again.');
    } finally {
      setReleasingJob(null);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setShowEditModal(true);
  };

  const handleCreateJob = () => {
    setSelectedJob(null);
    setShowCreateModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'RELEASED':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'ERROR':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatScheduledDate = (date) => {
    if (!date) return 'N/A';
    const scheduledDate = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffTime = scheduledDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `In ${diffDays} days`;
    
    return scheduledDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Scheduled Jobs</h1>
            <p className="text-gray-600">Manage and monitor future delivery jobs</p>
          </div>
          <button
            onClick={handleCreateJob}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Schedule Job
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search scheduled jobs by route or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="RELEASED">Released</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="ERROR">Error</option>
          </select>
        </div>
        <div>
          <select
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Drivers</option>
            {availableDrivers.map(driver => (
              <option key={driver.id} value={driver.id}>
                {driver.firstName} {driver.lastName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setDriverFilter('all');
              setDateFilter('all');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
        <div className="text-sm text-gray-600">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} scheduled jobs
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
            <span className="ml-2 text-gray-600">Loading scheduled jobs...</span>
          </div>
        )}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver & Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status & Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route & Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scheduledJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {job.description || `Scheduled Job #${job.id}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          {job.category || 'Standard'} • {job.description?.substring(0, 50) || 'No description'}
                        </div>
                        <div className="text-xs text-gray-400">
                          Created: {job.createdAt ? new Date(job.createdAt.toDate ? job.createdAt.toDate() : job.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-gray-900">
                          <strong>Driver:</strong> {job.driver?.name || 'Unknown Driver'}
                        </div>
                        <div className="text-sm text-gray-500">
                          <FaCalendar className="inline mr-1" />
                          {job.scheduledDate ? new Date(job.scheduledDate.toDate ? job.scheduledDate.toDate() : job.scheduledDate).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400">
                          <FaClock className="inline mr-1" />
                          {formatScheduledDate(job.scheduledDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                          {job.status || 'UNKNOWN'}
                        </span>
                        {job.priority && (
                          <div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                              {job.priority}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-gray-900">
                          <strong>Route:</strong> {job.route || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          <strong>From:</strong> {job.pickupLocation || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          <strong>To:</strong> {job.deliveryLocation || 'N/A'}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          ${job.price || 0}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewJob(job)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                        {job.status === 'PENDING' && (
                          <button 
                            onClick={() => handleReleaseJob(job.id)}
                            disabled={releasingJob === job.id}
                            className="text-green-600 hover:text-green-900"
                            title="Release Job"
                          >
                            {releasingJob === job.id ? (
                              <FaSpinner className="h-4 w-4 animate-spin" />
                            ) : (
                              <FaPlay className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        <button 
                          onClick={() => handleEditJob(job)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Job"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteJob(job)}
                          disabled={deletingJob === job.id || job.status !== 'PENDING'}
                          className={`${
                            job.status === 'PENDING'
                              ? 'text-red-600 hover:text-red-900'
                              : 'text-gray-400 cursor-not-allowed'
                          } disabled:opacity-50 transition-colors`}
                          title={
                            job.status === 'PENDING'
                              ? 'Delete Job'
                              : 'Cannot delete jobs that are not pending'
                          }
                        >
                          {deletingJob === job.id ? (
                            <FaSpinner className="h-4 w-4 animate-spin" />
                          ) : (
                            <FaTrash className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {scheduledJobs.length === 0 && !loading && (
          <div className="text-center py-12">
            <FaTruck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No scheduled jobs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || driverFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'No scheduled jobs have been created yet. Get started by scheduling a new job.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Job Form Modal */}
      <JobFormModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        isScheduled={true}
        onSuccess={() => {
          fetchScheduledJobs();
        }}
      />

      {/* Job Details Modal */}
      <JobDetailsModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        isScheduled={true}
      />

      {/* Delete Job Modal */}
      <DeleteJobModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        onDelete={handleConfirmDelete}
        isScheduled={true}
      />
    </div>
  );
};

export default AdminScheduledJobs;
